import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useAtomValue } from 'jotai';
import { X } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import TransitionScreen from '@/components/transitionScreen';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { FormControl, FormControlError, FormControlErrorText } from '@/components/ui/form-control';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
} from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { userIdAtom } from '@/services/globalState';
import { submitAuthenticatedQuestion } from '@/services/submitAuthenticatedQuestion';
import { segmentClient } from '@/util/analytics';
import { getPlatformStorage } from '@/util/platformStorage';

const SCREEN_COLUMN = getScreenColumnClasses({ gutter: 'px-5 web:px-0' });

const EXAMPLE_PROMPTS = [
  {
    title: 'Ask about a tough case',
    text: 'I have a [age]-year-old patient with [diagnosis, stage, and relevant biomarkers] who has progressed through [prior treatments]. I am considering [treatment options you are weighing], but I am uncertain about sequencing and patient selection. Given their [performance status, comorbidities, or other relevant factors], what approach would you take and why?',
  },
  {
    title: 'Ask about new data',
    text: 'Recent data from [trial name or conference, if known] has introduced new options for [cancer type and subtype]. How has this changed your real-world treatment approach? Which patients are you applying this to in practice, and are there any you would still treat differently?',
  },
  {
    title: 'Ask about an emerging treatment',
    text: 'I am interested in understanding how [treatment or drug class] is being incorporated into practice for [cancer type and subtype]. What has your experience been with efficacy and tolerability? Are there specific patient profiles where you see the most benefit?',
  },
];

const PLACEHOLDER_TEXT = `Example prompt:\n\nI have a 58-year-old patient with EGFR exon 19 deletion-positive stage IV NSCLC who has progressed through osimertinib and platinum-based chemotherapy. I am considering amivantamab-vmjw plus lazertinib versus docetaxel plus ramucirumab, but I am uncertain about sequencing and patient selection. Given their ECOG 1 status and history of mild interstitial lung disease, what approach would you take and why?`;

const TYPING_SPEED_MS = 18;

// The typewriter effect does not work reliably on iOS/Android. For web only.
function useTypewriterPlaceholder() {
  const [displayedText, setDisplayedText] = useState(isWeb ? '' : PLACEHOLDER_TEXT);

  const [isFocused, setIsFocused] = useState(false);

  const indexRef = useRef(isWeb ? 0 : PLACEHOLDER_TEXT.length);

  useEffect(() => {
    if (!isWeb || isFocused) {
      return;
    }

    if (indexRef.current >= PLACEHOLDER_TEXT.length) {
      return;
    }

    const timer = setInterval(() => {
      indexRef.current += 1;

      setDisplayedText(PLACEHOLDER_TEXT.slice(0, indexRef.current));

      if (indexRef.current >= PLACEHOLDER_TEXT.length) {
        clearInterval(timer);
      }
    }, TYPING_SPEED_MS);

    return () => clearInterval(timer);
  }, [isFocused]);

  const onFocus = useCallback(() => {
    setIsFocused(true);

    setDisplayedText(PLACEHOLDER_TEXT);

    indexRef.current = PLACEHOLDER_TEXT.length;
  }, []);

  return { placeholder: displayedText, onFocus };
}

const formSchema = z.object({
  message: z
    .string()
    .min(1, 'Please enter your question')
    .refine((val) => val.trim().length >= 50, {
      message: 'Please enter at least 50 characters',
    }),
});

type FormValues = z.infer<typeof formSchema>;

function NewQuestionContent() {
  const { placeholder: animatedPlaceholder, onFocus: onPlaceholderFocus } =
    useTypewriterPlaceholder();

  const { fromSignupFunnel, email } = useLocalSearchParams<{
    fromSignupFunnel?: string;
    email?: string;
  }>();

  const isFromSignupFunnel = fromSignupFunnel === 'true';

  const { navigate, replace } = useRouter();

  const { colors } = useColors();

  const { bottom } = useSafeAreaInsets();

  const toast = useToaster();

  const hasTrackedView = useRef(false);

  const userId = useAtomValue(userIdAtom);

  const isAuthenticated = !!userId;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showExamplesModal, setShowExamplesModal] = useState(false);

  const modalSize =
    useBreakpointValue({
      default: 'full',
      md: 'md',
    }) || 'md';

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      message: '',
    },
    resolver: zodResolver(formSchema),
  });

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: submitAuthenticatedQuestion,
  });

  const draftLoaded = useRef(false);

  useEffect(() => {
    const loadDraft = async () => {
      try {
        const storage = getPlatformStorage(undefined, 'local');

        const draft = await storage.getItem(STORAGE_KEYS.NEW_QUESTION_DRAFT);

        if (draft) {
          setValue('message', draft);
        }
      } catch {}

      draftLoaded.current = true;
    };

    loadDraft();
  }, [setValue]);

  const messageValue = watch('message');

  useEffect(() => {
    if (!draftLoaded.current) {
      return;
    }

    const saveDraft = async () => {
      try {
        const storage = getPlatformStorage(undefined, 'local');

        await storage.setItem(STORAGE_KEYS.NEW_QUESTION_DRAFT, messageValue || '');
      } catch {}
    };

    saveDraft();
  }, [messageValue]);

  useFocusEffect(
    useCallback(() => {
      if (hasTrackedView.current) {
        return;
      }

      hasTrackedView.current = true;

      segmentClient.track(ANALYTICS_EVENTS.NEW_QUESTION_FORM_VIEWED, {
        is_authenticated: isAuthenticated,
        from_signup_funnel: isFromSignupFunnel,
      });
    }, [isAuthenticated])
  );

  const onSubmit = handleSubmit(async () => {
    const message = (messageValue || '').trim();

    segmentClient.track(ANALYTICS_EVENTS.NEW_QUESTION_FORM_SUBMITTED, {
      message_length: message.length,
      is_authenticated: isAuthenticated,
      from_signup_funnel: isFromSignupFunnel,
    });

    if (isAuthenticated) {
      try {
        setIsSubmitting(true);

        await submitQuestion({
          message,
        });

        const storage = getPlatformStorage(undefined, 'local');

        await storage.removeItem(STORAGE_KEYS.NEW_QUESTION_DRAFT);

        replace('/(open)/new-question/success');
      } catch (error) {
        setIsSubmitting(false);

        toast.showError({
          title: 'Could not submit your question. Please try again.',
        });
      }
    } else {
      navigate({
        pathname: ROUTES.NEW_QUESTION_GET_CONTACT_INFO,
        params: email ? { email } : undefined,
      });
    }
  });

  if (isSubmitting) {
    return <TransitionScreen message="Submitting your question ..." />;
  }

  return (
    <VStack
      className={twJoin('flex-1', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      {isWeb ? null : (
        <Header
          contentScrollsUnderHeader
          isBackButton
          title="New Question"
          statusBarBackgroundColor={colors.background[0]}
        />
      )}
      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={12}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={twJoin(
          'flex-col flex-grow pt-6 pb-6 web:pt-6 web:pb-10',
          SCREEN_COLUMN.content
        )}>
        <VStack className="mb-6">
          <Text size="xl" className="mb-3 font-bold text-typography-900">
            Access Oncology Expertise
          </Text>
          <Text size="sm" className="text-typography-700">
            Submit your question and receive a personalized response from a leading specialist
            within 24 hours.
          </Text>
        </VStack>
        <VStack space="md">
          <Controller
            name="message"
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <FormControl isInvalid={!!errors.message?.message && isSubmitted}>
                <Textarea style={{ minHeight: 250 }}>
                  <TextareaInput
                    placeholder={animatedPlaceholder}
                    placeholderTextColor={colors.typography[600]}
                    style={{ color: colors.typography[900] }}
                    className="text-base"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    onFocus={onPlaceholderFocus}
                    multiline
                    textAlignVertical="top"
                  />
                </Textarea>
                <FormControlError className="mt-2">
                  <FormControlErrorText size="xs">{errors.message?.message}</FormControlErrorText>
                </FormControlError>
                <Button
                  variant="link"
                  className="mt-3 self-start"
                  onPress={() => setShowExamplesModal(true)}>
                  <ButtonText size="sm" className="font-semibold text-primary-600 underline">
                    Need help or pointers on how to word your inquiry?
                  </ButtonText>
                </Button>
              </FormControl>
            )}
          />
        </VStack>
        <VStack className="flex-1" />
        <Button
          size="xl"
          className="mt-8 w-full gap-x-2 web:mt-10"
          onPress={onSubmit}
          disabled={isPending}>
          {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
          <ButtonText size="md">{isAuthenticated ? 'Submit' : 'Continue'}</ButtonText>
        </Button>
        {isFromSignupFunnel ? (
          <Link href={ROUTES.DIRECTORY_TAB} asChild>
            <Button
              variant="link"
              className="mt-3 w-full"
              onPress={() => {
                segmentClient.track(ANALYTICS_EVENTS.NEW_QUESTION_FORM_SKIPPED, {
                  user_id: userId,
                  from_signup_funnel: isFromSignupFunnel,
                });
              }}>
              <ButtonText size="sm" className="text-typography-600">
                Skip for now
              </ButtonText>
            </Button>
          </Link>
        ) : null}
      </KeyboardAwareScrollView>
      <Modal
        isOpen={showExamplesModal}
        size={modalSize}
        onClose={() => setShowExamplesModal(false)}>
        <ModalBackdrop />
        <ModalContent className="max-h-[85vh]">
          <ModalHeader className="pb-4">
            <Heading size="h2-md">Example Inquiries</Heading>
            <ModalCloseButton onPress={() => setShowExamplesModal(false)}>
              <Icon as={X} size="md" />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <ScrollView showsVerticalScrollIndicator={false}>
              <VStack space="lg">
                {EXAMPLE_PROMPTS.map((prompt, index) => (
                  <VStack
                    key={index}
                    className="rounded-xl border border-outline-200 bg-background-50 p-4">
                    <Text size="sm" className="mb-2 font-bold text-typography-900">
                      {prompt.title}
                    </Text>
                    <Text size="sm" className="text-typography-800">
                      {prompt.text}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3 self-start"
                      onPress={() => {
                        setValue('message', prompt.text);

                        setShowExamplesModal(false);
                      }}>
                      <ButtonText size="xs">Use this prompt</ButtonText>
                    </Button>
                  </VStack>
                ))}
              </VStack>
            </ScrollView>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default function NewQuestionIndex() {
  if (isWeb) {
    return (
      <VStack className="h-screen w-full items-center justify-center">
        <VStack className="w-full px-5" style={{ maxWidth: 540 }}>
          <NewQuestionContent />
        </VStack>
      </VStack>
    );
  }

  return <NewQuestionContent />;
}
